import React from 'react';
import { Box, Typography, Button, Grid, Container } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import heroImg from '../../../images/school_mgmt_hero.png';

const badges = ['200+ Schools', 'Cloud-Based', 'Parent App'];

const Hero = () => (
  <Box sx={{
    bgcolor: '#f8fafc',
    backgroundImage: 'radial-gradient(circle at 85% 15%, rgba(37,99,235,0.07) 0%, transparent 45%), radial-gradient(circle at 5% 85%, rgba(104,77,244,0.05) 0%, transparent 45%)',
    minHeight: { xs: 'auto', md: '68vh' },
    display: 'flex',
    alignItems: 'center',
    py: { xs: 10, md: 5 },
    position: 'relative',
    overflow: 'hidden',
    borderBottom: '1px solid #e2e8f0',
  }}>

    {/* Subtle dot grid */}
    <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(37,99,235,0.1) 1px, transparent 1px)', backgroundSize: '26px 26px', pointerEvents: 'none' }} />

    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      <Grid container spacing={6} alignItems="center">

        {/* ── LEFT: Text content ── */}
        <Grid item xs={12} md={6}>
          {/* Pill badge */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '40px', px: 2.5, py: 1, mb: 4 }}>
            <SchoolIcon sx={{ color: '#2563eb', fontSize: 18 }} />
            <Typography sx={{ color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, letterSpacing: 1.2, fontFamily: "'Montserrat', sans-serif", textTransform: 'uppercase' }}>
              Complete School ERP Software
            </Typography>
          </Box>

          <Typography variant="h1" sx={{ color: '#0f172a', fontWeight: 900, fontSize: { xs: '2.2rem', md: '3.5rem' }, lineHeight: 1.12, mb: 3, fontFamily: "'Montserrat', sans-serif" }}>
            Run Your School{' '}
            <Box component="span" sx={{ color: '#2563eb' }}>100% Digitally</Box>
          </Typography>

          <Typography sx={{ color: '#475569', fontSize: 17, mb: 5, lineHeight: 1.8, fontFamily: "'Montserrat', sans-serif", maxWidth: 490 }}>
            From admissions and attendance to exams, fees, and staff payroll — SN Info Systems handles everything in one seamless platform.
          </Typography>

          {/* Buttons */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 5 }}>
            <Button variant="contained" endIcon={<ArrowForwardIcon />} size="large"
              sx={{ bgcolor: '#2563eb', color: '#fff', px: 5, py: 1.7, borderRadius: '14px', fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 16, boxShadow: '0 10px 28px rgba(37,99,235,0.3)', '&:hover': { bgcolor: '#1d4ed8', transform: 'translateY(-2px)' }, transition: 'all 0.3s' }}>
              Book Free Demo
            </Button>
            <Button variant="outlined" startIcon={<PhoneIcon />} size="large"
              sx={{ borderColor: '#bfdbfe', bgcolor: '#fff', color: '#1d4ed8', px: 5, py: 1.7, borderRadius: '14px', fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 16, '&:hover': { borderColor: '#2563eb', bgcolor: '#eff6ff' }, transition: 'all 0.3s' }}>
              Talk to Us
            </Button>
          </Box>

          {/* Trust badges */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {badges.map(b => (
              <Box key={b} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#2563eb', fontSize: 17 }} />
                <Typography sx={{ color: '#475569', fontSize: 14, fontWeight: 600, fontFamily: "'Montserrat', sans-serif" }}>{b}</Typography>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* ── RIGHT: Small, attractive image in browser mockup ── */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'relative', maxWidth: 520, mx: 'auto' }}>

            {/* Decorative rotated card behind */}
            <Box sx={{
              position: 'absolute', inset: '-3% -2%',
              borderRadius: '28px',
              background: 'linear-gradient(135deg, #dbeafe 0%, #ede9fe 100%)',
              transform: 'rotate(-3deg)',
              zIndex: 0,
            }} />

            {/* Browser chrome frame  ── this constrains the image naturally */}
            <Box sx={{
              position: 'relative', zIndex: 1,
              borderRadius: '18px',
              bgcolor: '#ffffff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 24px 60px rgba(37,99,235,0.1)',
              overflow: 'hidden',
              transition: 'transform 0.4s',
              '&:hover': { transform: 'translateY(-6px)' },
            }}>
              {/* Fake browser top bar */}
              <Box sx={{ bgcolor: '#f1f5f9', px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 0.8, borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#22c55e' }} />
                <Box sx={{ flex: 1, bgcolor: '#fff', borderRadius: '6px', px: 2, py: 0.5, ml: 1.5, border: '1px solid #e2e8f0' }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: 11, fontFamily: 'monospace' }}>school.sninfosystems.com</Typography>
                </Box>
              </Box>
              {/* ── Image: maxHeight keeps it from being huge ── */}
              <img
                src={heroImg}
                alt="School Management Dashboard"
                style={{ width: '100%', display: 'block', maxHeight: 340, objectFit: 'cover', objectPosition: 'top' }}
              />
            </Box>

            {/* Floating stats badge — bottom left */}
            <Box sx={{
              position: 'absolute', bottom: -18, left: -16, zIndex: 2,
              bgcolor: '#ffffff', borderRadius: '16px', p: 1.8,
              boxShadow: '0 8px 30px rgba(37,99,235,0.15)',
              border: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SchoolIcon sx={{ color: '#2563eb', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#0f172a', fontWeight: 900, fontSize: '1.05rem', fontFamily: "'Montserrat', sans-serif", lineHeight: 1 }}>50,000+</Typography>
                <Typography sx={{ color: '#64748b', fontSize: 11, fontFamily: "'Montserrat', sans-serif" }}>Students Managed</Typography>
              </Box>
            </Box>

            {/* Floating uptime badge — top right */}
            <Box sx={{
              position: 'absolute', top: -14, right: -14, zIndex: 2,
              bgcolor: '#ffffff', borderRadius: '14px', px: 2, py: 1,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 6px #22c55e' }} />
              <Typography sx={{ color: '#0f172a', fontWeight: 800, fontSize: 13, fontFamily: "'Montserrat', sans-serif" }}>99% Uptime</Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default Hero;
